import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Button } from 'antd'
import { verifySignupToken, markActive } from '@/features/auth/api'
import { setSession } from '@/lib/session'
import { buildAuthSession } from '../session'
import type { VerifySignupTokenResponse } from '../types'
import AuthLinkLayout from '../components/AuthLinkLayout'
import SetPasswordForm from '../components/SetPasswordForm'
import { clearExtensionIntent, postAuthSuccessToExtension, restoreExtensionIntent } from '../extensionAuth'

export default function ConfirmSignupPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invalidLink, setInvalidLink] = useState(false)
  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setInvalidLink(true)
      setLoading(false)
      return
    }
    verifySignupToken(id)
      .then((res: VerifySignupTokenResponse) => {
        if (res.message && ['activated', 'expired', 'invalid'].includes(res.message)) {
          setInvalidLink(true)
          setError(`Link has been ${res.message}.`)
        } else {
          setEmail(res.email ?? null)
        }
      })
      .catch(() => setInvalidLink(true))
      .finally(() => setLoading(false))
  }, [id])

  const onFinish = async (values: { password: string; confirmPassword: string }) => {
    if (!id || !email) return
    setError(null)
    setSubmitting(true)
    try {
      const res = await markActive({ email, password: values.password, token: id })
      if (res?.message === 'SignInRequired') {
        // Authenticator-app users get no tokens here; they sign in with their password and code.
        navigate('/login', { replace: true })
        return
      }
      if (res?.idToken && res?.refreshToken) {
        const nextSession = await buildAuthSession(
          email,
          res.idToken,
          res.refreshToken,
          res.expiry ?? 3600
        )
        setSession(nextSession)
        const extensionIntent = restoreExtensionIntent()
        if (extensionIntent.isExtension) {
          postAuthSuccessToExtension({
            idToken: nextSession.idToken,
            refreshToken: nextSession.refreshToken,
            tokenExpiry: nextSession.tokenExpiry,
          })
          clearExtensionIntent()
          if (extensionIntent.redirectUri) {
            window.location.href = extensionIntent.redirectUri
            return
          }
        }
        navigate('/', { replace: true })
      } else {
        setError('An error occurred while completing signup. Please try again.')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An error occurred. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLinkLayout
      loading={loading}
      invalidLink={invalidLink}
      invalidTitle="Invalid or expired link"
      invalidDescription="This signup link is no longer valid. You may request a new one or sign in if you already have an account."
      invalidError={error}
      invalidActions={
        <div className="flex gap-3">
          <Link to="/login">
            <Button type="primary">Go to sign in</Button>
          </Link>
          <Link to="/signup">
            <Button>Sign up</Button>
          </Link>
        </div>
      }
    >
      <SetPasswordForm
        email={email}
        passwordLabel="Password"
        submitLabel="Complete signup"
        onSubmit={onFinish}
        loading={submitting}
        error={error}
      />
    </AuthLinkLayout>
  )
}
